import { useEffect, useRef, useState } from 'react';
import { isRequestCanceled } from '../utils/api';

/** Each query owns its timer and request, including the debounce interval. */
export function usePickerSearch<T>(
  scope: string,
  load: (search: string, signal: AbortSignal) => Promise<T[]>,
  enabled = true,
  initialSearch = '',
) {
  const [search, setSearch] = useState(initialSearch);
  const [result, setResult] = useState<{ scope: string; search: string; options: T[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [retry, setRetry] = useState(0);
  const latest = useRef({ scope, search, enabled });
  latest.current = { scope, search, enabled };

  useEffect(() => {
    const controller = new AbortController();
    const isCurrent = () => !controller.signal.aborted && latest.current.scope === scope
      && latest.current.search === search && latest.current.enabled === enabled;
    setResult(null);
    setError(null);
    setLoading(enabled);
    if (!enabled) return () => controller.abort();
    const timer = setTimeout(async () => {
      try {
        const result = await load(search, controller.signal);
        if (isCurrent()) setResult({ scope, search, options: result });
      } catch (err) {
        if (isCurrent() && !isRequestCanceled(err)) setError(err);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [scope, search, enabled, load, retry]);

  const loaded = enabled && result?.scope === scope && result.search === search;
  const options = loaded ? result.options : EMPTY_OPTIONS as T[];
  return { search, setSearch, options, loaded, loading, error, retry: () => setRetry(value => value + 1) };
}

const EMPTY_OPTIONS: never[] = [];
